export const pageLayerLabels = {
  "reading-box": "Reading box",
  background: "Background artwork",
  backdrop: "Blurred background",
  "page-color": "Page color",
} as const;
export type PageLayerId = keyof typeof pageLayerLabels;
export type LayerDevice = "desktop" | "mobile";
export const isPageLayer = (id: string): id is PageLayerId =>
  Object.hasOwn(pageLayerLabels, id);
export interface LayerField {
  key: string;
  label: string;
  type: "number" | "color" | "checkbox" | "select" | "image";
  min?: number;
  max?: number;
  step?: number;
  choices?: string[];
  fallback: any;
}
export function pageLayerFields(
  id: PageLayerId,
  device: LayerDevice,
): LayerField[] {
  const number = (
    key: string,
    label: string,
    min: number,
    max: number,
    fallback: number,
    step = 1,
  ): LayerField => ({ key, label, type: "number", min, max, fallback, step });
  if (id === "page-color")
    return [
      {
        key: "secondary",
        label: "Fill color",
        type: "color",
        fallback: "#f0e6d5",
      },
    ];
  if (id === "background")
    return [
      { key: device, label: "Image", type: "image", fallback: "" },
      number("backgroundOpacity", "Opacity", 0, 1, 1, 0.01),
      number("darkness", "Darkness", 0, 0.8, 0.18, 0.01),
    ];
  if (id === "backdrop")
    return [
      {
        key: "blurredBackground",
        label: "Image source",
        type: "select",
        choices: ["same", "custom", "none"],
        fallback: "same",
      },
      {
        key: "blurredImage",
        label: "Custom image",
        type: "image",
        fallback: "",
      },
      number("x", "Horizontal focal point (%)", 0, 100, 50),
      number("y", "Vertical focal point (%)", 0, 100, 50),
    ];
  return [
    number(
      device === "mobile" ? "panelMobileX" : "panelX",
      "Horizontal alignment (%)",
      0,
      100,
      50,
    ),
    number(
      device === "mobile" ? "panelMobileTop" : "panelTop",
      "Top spacing (px)",
      0,
      1200,
      56,
    ),
    number(
      device === "mobile" ? "panelMobileWidth" : "panelDesktopWidth",
      "Width (%)",
      10,
      100,
      100,
    ),
    number(
      device === "mobile" ? "panelMobileFreeX" : "panelDesktopFreeX",
      "Horizontal offset (% page width)",
      -1000,
      1000,
      0,
    ),
    number("panelWidth", "Maximum width (px)", 240, 2000, 820),
    number(
      device === "mobile" ? "panelMobileHeight" : "panelDesktopHeight",
      "Minimum height (% page width)",
      0,
      200,
      0,
    ),
    number(
      device === "mobile" ? "panelMobileOffset" : "panelDesktopOffset",
      "Vertical offset (% page width)",
      -100,
      200,
      0,
    ),
    number(
      device === "mobile" ? "panelMobilePadding" : "panelPadding",
      "Padding (px)",
      0,
      device === "mobile" ? 80 : 160,
      22,
    ),
    { key: "surface", label: "Fill color", type: "color", fallback: "#fffaf0" },
    number("opacity", "Opacity", 0, 1, 0.96, 0.01),
    number("radius", "Corner radius (px)", 0, 40, 20),
    { key: "panelBorder", label: "Stroke", type: "checkbox", fallback: true },
    number("panelStrokeWidth", "Stroke width (px)", 0, 40, 1),
    {
      key: "panelStrokeColor",
      label: "Stroke color",
      type: "color",
      fallback: "#f0e6d5",
    },
    { key: "panelShadow", label: "Shadow", type: "checkbox", fallback: true },
  ];
}
export function pageLayerState(
  theme: any,
  device: LayerDevice,
  id: PageLayerId,
) {
  const state = theme.pageLayers?.[device]?.[id];
  return {
    present: state?.present !== false,
    hidden: state?.hidden === true,
    values: state?.values ?? {},
  };
}
export function validatePageLayers(input: any) {
  if (
    input !== undefined &&
    (!input || typeof input !== "object" || Array.isArray(input))
  )
    throw Error("Invalid page layers");
  return Object.fromEntries(
    (["desktop", "mobile"] as const).map((device) => {
      const group = input?.[device];
      if (
        group !== undefined &&
        (!group ||
          typeof group !== "object" ||
          Array.isArray(group) ||
          Object.keys(group).some((id) => !isPageLayer(id)))
      )
        throw Error("Invalid page layer group");
      return [
        device,
        Object.fromEntries(
          (Object.keys(pageLayerLabels) as PageLayerId[]).map((id) => {
            const state = group?.[id];
            if (
              state !== undefined &&
              (!state ||
                typeof state !== "object" ||
                Array.isArray(state) ||
                ["present", "hidden"].some(
                  (key) =>
                    state[key] !== undefined && typeof state[key] !== "boolean",
                ))
            )
              throw Error("Invalid page layer state");
            const values = state?.values ?? {};
            const fields = pageLayerFields(id, device);
            if (
              !values ||
              typeof values !== "object" ||
              Array.isArray(values) ||
              Object.keys(values).some(
                (key) => !fields.some((field) => field.key === key),
              )
            )
              throw Error("Invalid page layer settings");
            for (const [key, value] of Object.entries(values)) {
              const field = fields.find((item) => item.key === key)!;
              const valid =
                field.type === "number"
                  ? typeof value === "number" &&
                    Number.isFinite(value) &&
                    value >= field.min! &&
                    value <= field.max!
                  : field.type === "color"
                    ? typeof value === "string" && /^#[\da-f]{6}$/i.test(value)
                    : field.type === "checkbox"
                      ? typeof value === "boolean"
                      : field.type === "select"
                        ? field.choices!.includes(value as string)
                        : typeof value === "string" &&
                          (!value ||
                            /^\/media\/[a-f0-9-]+$/.test(value) ||
                            /^\/art\/[a-z0-9-]+\.(webp|png|svg)$/.test(value));
              if (!valid) throw Error(`Invalid ${field.label.toLowerCase()}`);
            }
            return [
              id,
              {
                present: state?.present !== false,
                hidden: state?.hidden === true,
                values: { ...values },
              },
            ];
          }),
        ),
      ];
    }),
  );
}
export function effectivePageTheme(theme: any, device: LayerDevice) {
  const values = Object.keys(pageLayerLabels).map(
    (id) => pageLayerState(theme, device, id as PageLayerId).values,
  );
  return Object.assign({}, theme, ...values);
}
export function pageRenderTheme(theme: any, device: LayerDevice) {
  const effective = effectivePageTheme(theme, device);
  for (const key of [
    "surface",
    "opacity",
    "radius",
    "panelBorder",
    "panelShadow",
  ])
    effective[key] = theme[key];
  return effective;
}
interface LayerSurface {
  style: {
    setProperty: (key: string, value: string, priority?: string) => void;
    removeProperty: (key: string) => string;
    getPropertyPriority: (key: string) => string;
    backgroundColor: string;
    background: string;
    border: string;
    boxShadow: string;
    borderRadius: string;
  };
  dataset: Record<string, string | undefined>;
}
export function applyPageLayerPresentation(
  background: LayerSurface,
  panel: LayerSurface | null,
  theme: any,
  device: LayerDevice,
  scene = background,
) {
  const visible = (id: PageLayerId) => {
    const state = pageLayerState(theme, device, id);
    return state.present && !state.hidden;
  };
  background.style.setProperty(
    "--artwork-display",
    visible("background") ? "block" : "none",
  );
  if (!visible("backdrop"))
    background.style.setProperty("--blur-display", "none", "important");
  else if (background.style.getPropertyPriority("--blur-display"))
    background.style.removeProperty("--blur-display");
  background.dataset.backgroundVisible = String(visible("background"));
  if (!visible("page-color")) {
    background.style.setProperty("--background-base", "transparent");
    scene.style.backgroundColor = "transparent";
    scene.dataset.layerColorHidden = "true";
  } else if (scene.dataset.layerColorHidden) {
    scene.style.removeProperty("background-color");
    delete scene.dataset.layerColorHidden;
  }
  if (panel) {
    const settings = effectivePageTheme(theme, device);
    const values = pageLayerState(theme, device, "reading-box").values;
    scene.style.setProperty(
      "--reading-free-x",
      String(
        (device === "mobile"
          ? settings.panelMobileFreeX
          : settings.panelDesktopFreeX) ?? 0,
      ),
    );
    scene.style.setProperty(
      "--reading-top",
      String(
        device === "mobile" ? settings.panelMobileTop : settings.panelTop,
      ) + "px",
    );
    scene.style.setProperty(
      "--reading-offset",
      String(
        (device === "mobile"
          ? settings.panelMobileOffset
          : settings.panelDesktopOffset) ?? 0,
      ),
    );
    panel.dataset.panelVisible = String(visible("reading-box"));
    if (!visible("reading-box")) {
      panel.style.background = "transparent";
      panel.style.border = "none";
      panel.style.boxShadow = "none";
    } else {
      if (values.surface !== undefined || values.opacity !== undefined)
        panel.style.background = `color-mix(in srgb,${settings.surface} ${settings.opacity * 100}%,transparent)`;
      if (values.radius !== undefined) {
        panel.style.setProperty("--radius", `${settings.radius}px`);
        panel.style.borderRadius = `${settings.radius}px`;
      }
      if (values.panelShadow !== undefined)
        panel.style.boxShadow = settings.panelShadow
          ? "0 24px 80px #0002"
          : "none";
      if (
        values.panelBorder !== undefined ||
        values.panelStrokeWidth !== undefined ||
        values.panelStrokeColor !== undefined
      )
        panel.style.border =
          settings.panelBorder === false
            ? "none"
            : `${settings.panelStrokeWidth ?? 1}px solid ${settings.panelStrokeColor ?? settings.secondary}`;
    }
  }
}
